import { z } from 'zod';
import { STEP_LABELS } from '../lib/provision-steps.js';
/**
 * `plugin_sources` row. Spec §4.1, §4.3, §5.5.
 * `ado_repo_id` is the stable ADO GUID and the primary webhook lookup key.
 */
export const pluginSourceStatusSchema = z.enum(['provisioning', 'active', 'failed']);
export const pluginSourceSchema = z.object({
    id: z.string().uuid(),
    adoRepoId: z.string().min(1),
    adoRepoName: z.string().min(1),
    url: z.string().url(),
    defaultBranch: z.string().default('main'),
    teamId: z.string().uuid().nullable(),
    status: pluginSourceStatusSchema,
    lastSyncSha: z.string().nullable(),
    lastSyncAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    deletedAt: z.string().datetime().nullable(),
});
/**
 * Step label written into `plugin_sources.provision_step` by the
 * `provision-team` worker's `markStepStarted` helper. Surfaced on the
 * admin shape so the dialog can drive per-pip progress live and so the
 * three-state timeout banner can distinguish "queue stuck" from
 * "still working" at the 90s polling boundary.
 *
 * Derived from {@link STEP_LABELS} so the enum, the runtime tuple, the
 * `plugin_sources_provision_step_chk` DB constraint, and the worker's
 * emit-side label set are a single source of truth. Adding a new label
 * requires updating `lib/provision-steps.ts` only — this schema, the
 * round-trip test, and the DB CHECK constraint pick it up automatically.
 */
export const provisionStepLabelSchema = z.enum(STEP_LABELS);
/**
 * Admin-view list-row shape. Webhook secrets are NEVER projected — the
 * route SELECTs a boolean `hasPendingSecret` so the admin UI can surface
 * the dual-secret rotation state without exposing the secret value. The
 * actual rotation is handled by `POST /api/sources/:id/rotate-webhook-secret`.
 *
 * `updatedAt`, `provisionStep`, and `provisionStepStartedAt` are exposed
 * for the ProvisionTeamDialog. The dialog uses `provisionStep` to drive
 * the live per-step pip progression (the worker writes it at every step
 * boundary) and falls back to the three-state timeout banner — distinguishing
 * "queue stuck", "still working", and "stale-failed" — at the 90s polling
 * boundary. `provisionStepStartedAt` powers per-step latency analytics via
 * the App Insights `provision.step_started` custom event. Both step fields
 * are tolerated as null on freshly-enqueued rows.
 */
export const pluginSourceAdminSchema = z.object({
    id: z.string().uuid(),
    adoRepoId: z.string().min(1),
    adoRepoName: z.string().min(1),
    teamId: z.string().uuid().nullable(),
    status: pluginSourceStatusSchema,
    lastSyncSha: z.string().nullable(),
    lastSyncAt: z.string().datetime().nullable(),
    hasPendingSecret: z.boolean(),
    updatedAt: z.string().datetime(),
    provisionStep: provisionStepLabelSchema.nullable(),
    provisionStepStartedAt: z.string().datetime().nullable(),
});
//# sourceMappingURL=source.js.map